import React, { useCallback, useEffect, useRef } from 'react';

import dayjs from 'dayjs';

import { usePermissions } from '@/modules/common';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';

import { ASSIGNMENT_BASE_OFFSET_REM, ASSIGNMENT_LEVEL_SPACING_REM } from '../../constants';
import { isDateRangeUnavailable, isDateUnavailable } from '../../helpers';
import usePlannerStore from '../../store';
import { RowItem } from '../../types';
import { useAutoScroll } from '../scroll/use-auto-scroll';

export const useAddAssignment = (row: RowItem, userStartDate?: dayjs.Dayjs) => {
  const { hasPermission } = usePermissions();
  const isCreateAssignmentEnabled = hasPermission(OpsiumPolicy.AssignmentsCreate);

  const dates = usePlannerStore(store => store.dates);
  const setSelectedRow = usePlannerStore(store => store.setSelectedRow);
  const setSelectedDates = usePlannerStore(store => store.setSelectedDates);
  const activeDragResizeId = usePlannerStore(store => store.activeDragResizeId);
  const activeCutId = usePlannerStore(store => store.activeCutId);
  const setShowUnavailableWarning = usePlannerStore(store => store.setShowUnavailableWarning);
  const setIsDragResizeInteracting = usePlannerStore(store => store.setIsDragResizeInteracting);
  const [isSelecting, setIsSelecting] = React.useState(false);
  const [startDayIndex, setStartDayIndex] = React.useState<number | null>(null);
  const [endDayIndex, setEndDayIndex] = React.useState<number | null>(null);
  const [isHovering, setIsHovering] = React.useState(false);
  const [hoveredDayIndex, setHoveredDayIndex] = React.useState<number | null>(null);
  const [hoveredLevel, setHoveredLevel] = React.useState<number>(0);

  const timelineRef = useRef<HTMLDivElement>(null);
  const lastEndDayIndexRef = useRef<number | null>(null);
  const autoScrollStarted = useRef<boolean>(false);
  const { lastMouseXRef, startAutoScroll, stopAutoScroll } = useAutoScroll();

  // Auto-scroll during drag-to-create selection
  useEffect(() => {
    if (!isSelecting) return;

    /** Calculate endDayIndex from current mouse position relative to timeline */
    const updateEndDayIndex = () => {
      if (!timelineRef.current || startDayIndex === null) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const x = lastMouseXRef.current - rect.left;
      const dayWidth = rect.width / dates.length;
      const idx = Math.max(0, Math.min(Math.floor(x / dayWidth), dates.length - 1));
      if (idx !== lastEndDayIndexRef.current) {
        lastEndDayIndexRef.current = idx;
        setEndDayIndex(idx);
      }
    };

    const handleDocumentMouseMove = (e: MouseEvent) => {
      lastMouseXRef.current = e.clientX;
      // Start auto-scroll only after user begins moving — prevents instant scroll when
      // mousedown happens near an edge
      if (!autoScrollStarted.current) {
        autoScrollStarted.current = true;
        startAutoScroll(() => {
          updateEndDayIndex();
        });
      }
      updateEndDayIndex();
    };

    // If user releases mouse outside the timeline, cancel the selection cleanly
    const handleDocumentMouseUp = () => {
      lastEndDayIndexRef.current = null;
      setIsDragResizeInteracting(false);
      setIsSelecting(false);
      setStartDayIndex(null);
      setEndDayIndex(null);
    };

    // Cancel assignment creation on Escape key press
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        lastEndDayIndexRef.current = null;
        setIsDragResizeInteracting(false);
        setIsSelecting(false);
        setStartDayIndex(null);
        setEndDayIndex(null);
        stopAutoScroll();
      }
    };

    document.addEventListener('mousemove', handleDocumentMouseMove);
    document.addEventListener('mouseup', handleDocumentMouseUp);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      stopAutoScroll();
      autoScrollStarted.current = false;
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isSelecting,
    startDayIndex,
    dates.length,
    startAutoScroll,
    stopAutoScroll,
    setIsDragResizeInteracting,
  ]);

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  const handleMouseLeaveTimeline = useCallback(() => {
    setIsHovering(false);
    setHoveredDayIndex(null);
  }, []);

  const calculateLevel = useCallback((y: number): number => {
    // Calculate which level the cursor is at based on Y position
    // Level 0 starts at ASSIGNMENT_BASE_OFFSET_REM
    // Each subsequent level is ASSIGNMENT_LEVEL_SPACING_REM below
    const REM_TO_PX = 16;
    const baseOffsetPx = ASSIGNMENT_BASE_OFFSET_REM * REM_TO_PX;
    const levelSpacingPx = ASSIGNMENT_LEVEL_SPACING_REM * REM_TO_PX;

    if (y < baseOffsetPx) return 0;

    const level = Math.floor((y - baseOffsetPx) / levelSpacingPx);
    return Math.max(0, Math.min(level, 5)); // Cap at 5 levels max
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Don't start selection if in cut mode
      if (activeCutId !== null) return;

      if (!isCreateAssignmentEnabled) return;

      if (
        e.target instanceof Element &&
        (e.target.id.includes('selection') ||
          e.target.id.includes('row') ||
          e.target.id.includes('phantom'))
      ) {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const dayWidth = rect.width / dates.length;
        const dayIndex = Math.floor(x / dayWidth);

        if (dayIndex >= 0 && dayIndex < dates.length) {
          lastMouseXRef.current = e.clientX;
          setIsDragResizeInteracting(true);
          setIsSelecting(true);
          setStartDayIndex(dayIndex);
          setEndDayIndex(dayIndex);
          setHoveredLevel(calculateLevel(y));
          setIsHovering(false);
        }
      }
    },
    [
      dates.length,
      calculateLevel,
      isCreateAssignmentEnabled,
      activeCutId,
      setIsDragResizeInteracting,
    ]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isCreateAssignmentEnabled) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const dayWidth = rect.width / dates.length;
      const currentDayIndex = Math.floor(x / dayWidth);

      // Update hovered day index and level for phantom positioning
      // Only show phantom when hovering over empty space (selection, row, or phantom div, not assignments)
      // Don't show phantom if in cut mode or if the date is unavailable (not joined / archived)
      const hoveredDate = dates[currentDayIndex];
      const isUnavailable =
        hoveredDate && isDateUnavailable(row.stateChanges, userStartDate, hoveredDate);

      if (
        !isSelecting &&
        activeCutId === null &&
        currentDayIndex >= 0 &&
        currentDayIndex < dates.length &&
        !isUnavailable &&
        e.target instanceof Element &&
        (e.target.id.includes('selection') ||
          e.target.id.includes('row') ||
          e.target.id.includes('phantom'))
      ) {
        setHoveredDayIndex(currentDayIndex);
        setHoveredLevel(calculateLevel(y));
      } else if (!isSelecting) {
        // Clear hovered index when over an assignment
        setHoveredDayIndex(null);
      }

      // Handle selection drag - update end day index to extend phantom
      if (
        isSelecting &&
        startDayIndex !== null &&
        currentDayIndex >= 0 &&
        currentDayIndex < dates.length &&
        currentDayIndex !== lastEndDayIndexRef.current
      ) {
        lastEndDayIndexRef.current = currentDayIndex;
        setEndDayIndex(currentDayIndex);
      }
    },
    [
      isSelecting,
      startDayIndex,
      dates,
      calculateLevel,
      activeCutId,
      row.stateChanges,
      userStartDate,
      isCreateAssignmentEnabled,
    ]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isCreateAssignmentEnabled) return;

      // Check if mouse up target is not part of the timeline cells
      if (!isSelecting || startDayIndex === null || endDayIndex === null) return;

      // Don't trigger create assignment modal if in cut mode
      if (activeCutId !== null) {
        lastEndDayIndexRef.current = null;
        setIsDragResizeInteracting(false);
        setIsSelecting(false);
        setStartDayIndex(null);
        setEndDayIndex(null);
        return;
      }

      // Check if target is within timeline (selection, row, phantom, or their children)
      const target = e.target as Element;
      const isValidTarget =
        target.id.includes('selection') ||
        target.id.includes('row') ||
        target.id.includes('phantom') ||
        target.closest('#phantom-assignment') !== null;

      if (isValidTarget) {
        // Use the start and end day indices from state
        const finalStartIndex = Math.min(startDayIndex, endDayIndex);
        const finalEndIndex = Math.max(startDayIndex, endDayIndex);

        if (finalStartIndex >= 0 && finalEndIndex < dates.length) {
          const startDate = dates[finalStartIndex];
          const endDate = dates[finalEndIndex];

          // Check if any date in the selected range is unavailable
          if (isDateRangeUnavailable(row.stateChanges, userStartDate, startDate, endDate)) {
            setShowUnavailableWarning(true);
          } else {
            setSelectedRow(row.id);
            setSelectedDates({
              startDate,
              endDate,
            });
          }
        }

        lastEndDayIndexRef.current = null;
        setIsDragResizeInteracting(false);
        setIsSelecting(false);
        setStartDayIndex(null);
        setEndDayIndex(null);
        setIsHovering(true);
      }
    },
    [
      isSelecting,
      startDayIndex,
      endDayIndex,
      dates,
      row.id,
      row.stateChanges,
      userStartDate,
      setSelectedRow,
      setSelectedDates,
      setShowUnavailableWarning,
      setIsDragResizeInteracting,
      isCreateAssignmentEnabled,
      activeCutId,
    ]
  );

  return {
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeaveTimeline,
    timelineRef,
    isHovering: isHovering && !isSelecting && activeDragResizeId === null,
    isSelecting,
    hoveredDayIndex,
    hoveredLevel,
    startDayIndex,
    endDayIndex,
  };
};
